// Use relative path in production (same domain), localhost in development
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || 'http://localhost:8000/api');

// Token storage keys
const ACCESS_TOKEN_KEY = 'statusreport_access_token';
const REFRESH_TOKEN_KEY = 'statusreport_refresh_token';
const NETWORK_ERROR_MESSAGE = 'Network error. Please check your connection and try again.';

let apiErrorNotifier = null;
let authInvalidHandler = null;

export const setApiErrorNotifier = (notifier) => {
  apiErrorNotifier = typeof notifier === 'function' ? notifier : null;
};

export const setAuthInvalidHandler = (handler) => {
  authInvalidHandler = typeof handler === 'function' ? handler : null;
};

const notifyAuthInvalid = () => {
  if (authInvalidHandler) {
    authInvalidHandler();
  }
};

const notifyApiError = (message) => {
  if (apiErrorNotifier && message) {
    apiErrorNotifier(message);
    return true;
  }
  return false;
};

const createApiError = (message, isNotified = false, status = null) => {
  const error = new Error(message);
  error.isNotified = isNotified;
  error.status = status;
  return error;
};

const throwNetworkError = () => {
  const wasNotified = notifyApiError(NETWORK_ERROR_MESSAGE);
  throw createApiError(NETWORK_ERROR_MESSAGE, wasNotified, 0);
};

const formatApiDetail = (detail) => {
  if (!detail) {
    return '';
  }

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }

        if (item && typeof item === 'object') {
          const loc = Array.isArray(item.loc) ? item.loc.join('.') : '';
          const msg = item.msg || item.message || '';
          if (loc && msg) {
            return `${loc}: ${msg}`;
          }
          if (msg) {
            return msg;
          }
        }

        return '';
      })
      .filter(Boolean);

    return messages.join(', ');
  }

  if (typeof detail === 'object') {
    return detail.message || detail.error || '';
  }

  return '';
};

// Token management
export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const setTokens = (access, refresh) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, access);
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
};

export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Base fetch with auth header
const authFetch = async (url, options = {}) => {
  const token = getAccessToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers,
    });
  } catch (error) {
    throwNetworkError();
  }

  // Handle 401 - try to refresh token
  if (response.status === 401 && getRefreshToken()) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Retry original request with new token
      headers['Authorization'] = `Bearer ${getAccessToken()}`;
      try {
        return await fetch(`${API_BASE_URL}${url}`, { ...options, headers });
      } catch (error) {
        throwNetworkError();
      }
    }
    notifyAuthInvalid();
  } else if (response.status === 401) {
    clearTokens();
    notifyAuthInvalid();
  }

  return response;
};

// Refresh access token
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
      if (data.refresh_token) {
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
      }
      return true;
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
  }

  clearTokens();
  notifyAuthInvalid();
  return false;
};

// Auth API
export const authAPI = {
  signup: async (userData) => {
    const response = await fetch(`${API_BASE_URL}/auth/signup/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      await throwApiError(response, 'Signup failed');
    }

    const data = await response.json();
    return data;
  },

  verifyEmail: async (token) => {
    const response = await fetch(`${API_BASE_URL}/auth/verify-email/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      await throwApiError(response, 'Email verification failed');
    }

    return response.json();
  },

  resendVerification: async (email) => {
    const response = await fetch(`${API_BASE_URL}/auth/resend-verification/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      await throwApiError(response, 'Failed to resend verification email');
    }

    return response.json();
  },

  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      await throwApiError(response, 'Login failed');
    }

    const data = await response.json();
    setTokens(data.tokens.access_token, data.tokens.refresh_token);
    return data.user;
  },

  logout: async () => {
    const refreshToken = getRefreshToken();
    try {
      await authFetch('/auth/logout/', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    clearTokens();
  },

  getMe: async () => {
    const response = await authFetch('/auth/me/');
    if (!response.ok) {
      await throwApiError(response, 'Failed to get user');
    }
    return response.json();
  },

  switchCompany: async (companyId) => {
    const response = await authFetch('/auth/switch-company/', {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to switch company');
    }
    return response.json();
  },

  updateDeviceName: async (deviceName) => {
    const response = await authFetch('/auth/device-name/', {
      method: 'PUT',
      body: JSON.stringify({ deviceName }),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to update device name');
    }
    return response.json();
  },

  getSessions: async () => {
    const response = await authFetch('/auth/sessions');
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch sessions');
    }
    return response.json();
  },

  terminateSession: async (sessionId, reason = 'no_longer_used') => {
    const response = await authFetch(`/auth/sessions/${sessionId}/terminate`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to terminate session');
    }
    return response.json();
  },

  terminateOtherSessions: async (reason = 'password_changed') => {
    const response = await authFetch('/auth/sessions/terminate-others', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to terminate other sessions');
    }
    return response.json();
  },

  changePassword: async ({ currentPassword, newPassword, confirmPassword, signOutOtherDevices = false }) => {
    const response = await authFetch('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        currentPassword,
        newPassword,
        confirmPassword,
        signOutOtherDevices,
      }),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to change password');
    }
    return response.json();
  },
};

export const settingsAPI = {
  get: async () => {
    const response = await authFetch('/settings');
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch settings');
    }
    return response.json();
  },

  update: async (payload) => {
    const response = await authFetch('/settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to update settings');
    }
    return response.json();
  },

  reset: async () => {
    const response = await authFetch('/settings/reset', {
      method: 'POST',
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to reset settings');
    }
    return response.json();
  },
};

const parseApiError = async (response, fallbackMessage) => {
  try {
    const errorPayload = await response.clone().json();
    const message = (
      formatApiDetail(errorPayload?.detail) ||
      formatApiDetail(errorPayload?.error) ||
      formatApiDetail(errorPayload?.message) ||
      response.statusText ||
      fallbackMessage
    );

    return message;
  } catch (jsonError) {
    // continue to text fallback below
  }

  try {
    const text = await response.text();
    if (text) {
      return text;
    }
  } catch (textError) {
    // no-op
  }

  return response.statusText || fallbackMessage;
};

const throwApiError = async (response, fallbackMessage, { notify = true } = {}) => {
  const message = await parseApiError(response, fallbackMessage);
  let wasNotified = false;
  if (notify) {
    wasNotified = notifyApiError(message);
  }
  throw createApiError(message, wasNotified, response.status);
};

export const usersAPI = {
  listByCompany: async (companyId, {
    excludeCompanyAdmin = false,
    includeSelf = false,
    roleFilter,
    search,
    sortBy,
    sortOrder,
    page,
    pageSize,
  } = {}) => {
    const params = new URLSearchParams({
      company_id: companyId,
      exclude_company_admin: String(excludeCompanyAdmin),
      include_self: String(includeSelf),
    });

    if (search) {
      params.set('search', search);
    }
    if (roleFilter) {
      params.set('role_filter', roleFilter);
    }
    if (sortBy) {
      params.set('sort_by', sortBy);
    }
    if (sortOrder) {
      params.set('sort_order', sortOrder);
    }
    if (page) {
      params.set('page', String(page));
    }
    if (pageSize) {
      params.set('page_size', String(pageSize));
    }

    const response = await authFetch(`/users?${params.toString()}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch users');
    }
    return response.json();
  },

  getStats: async (companyId) => {
    const response = await authFetch(`/users/stats?company_id=${encodeURIComponent(companyId)}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch user stats');
    }
    return response.json();
  },

  create: async (userData) => {
    const response = await authFetch('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to create user');
    }
    return response.json();
  },

  getById: async (userId, companyId) => {
    const response = await authFetch(`/users/${userId}?company_id=${encodeURIComponent(companyId)}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch user');
    }
    return response.json();
  },

  update: async (userId, companyId, userData) => {
    const response = await authFetch(`/users/${userId}?company_id=${encodeURIComponent(companyId)}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to update user');
    }
    return response.json();
  },

  remove: async (userId, companyId) => {
    const response = await authFetch(`/users/${userId}?company_id=${encodeURIComponent(companyId)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to delete user');
    }
    return response.json();
  },
};

export const groupsAPI = {
  listByCompany: async (companyId) => {
    const response = await authFetch(`/groups?company_id=${encodeURIComponent(companyId)}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch groups');
    }
    return response.json();
  },
};

export const permissionsAPI = {
  listBySubject: async ({ companyId, subjectType, subjectId }) => {
    const params = new URLSearchParams({
      company_id: companyId,
      subject_type: subjectType,
      subject_id: subjectId,
    });

    const response = await authFetch(`/permissions?${params.toString()}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch permissions');
    }
    return response.json();
  },

  bulkReplace: async ({ companyId, subjectType, subjectId, assignments }) => {
    const response = await authFetch('/permissions/bulk', {
      method: 'PUT',
      body: JSON.stringify({
        companyId,
        subjectType,
        subjectId,
        assignments,
      }),
    });

    if (!response.ok) {
      await throwApiError(response, 'Failed to save permissions');
    }
    return response.json();
  },
};

export const projectsAPI = {
  listByCompany: async (companyId, {
    statusFilter,
    search,
    sortBy,
    sortOrder,
    page,
    pageSize,
  } = {}) => {
    const params = new URLSearchParams({ company_id: companyId });
    if (statusFilter) {
      params.set('status_filter', statusFilter);
    }
    if (search) {
      params.set('search', search);
    }
    if (sortBy) {
      params.set('sort_by', sortBy);
    }
    if (sortOrder) {
      params.set('sort_order', sortOrder);
    }
    if (page) {
      params.set('page', String(page));
    }
    if (pageSize) {
      params.set('page_size', String(pageSize));
    }

    const response = await authFetch(`/projects?${params.toString()}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch projects');
    }
    return response.json();
  },

  getById: async (projectId) => {
    const response = await authFetch(`/projects/${projectId}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch project');
    }
    return response.json();
  },

  create: async (projectData) => {
    const response = await authFetch('/projects', {
      method: 'POST',
      body: JSON.stringify(projectData),
    });

    if (!response.ok) {
      await throwApiError(response, 'Failed to create project');
    }

    return response.json();
  },

  update: async (projectId, projectData) => {
    const response = await authFetch(`/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(projectData),
    });

    if (!response.ok) {
      await throwApiError(response, 'Failed to update project');
    }

    return response.json();
  },
};

export const tasksAPI = {
  listByProject: async (projectId, {
    statusFilter,
    dueDateFrom,
    dueDateTo,
    excludeNotStarted,
  } = {}) => {
    const params = new URLSearchParams({ project_id: String(projectId) });
    if (statusFilter) {
      params.set('status_filter', statusFilter);
    }
    if (dueDateFrom) {
      params.set('due_date_from', dueDateFrom);
    }
    if (dueDateTo) {
      params.set('due_date_to', dueDateTo);
    }
    if (excludeNotStarted) {
      params.set('exclude_not_started', 'true');
    }

    const response = await authFetch(`/tasks?${params.toString()}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch tasks');
    }
    return response.json();
  },

  create: async (taskData) => {
    const response = await authFetch('/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to create task');
    }
    return response.json();
  },

  update: async (taskId, taskData) => {
    const response = await authFetch(`/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(taskData),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to update task');
    }
    return response.json();
  },

  remove: async (taskId) => {
    const response = await authFetch(`/tasks/${taskId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to delete task');
    }
    return response.json();
  },

  createSubtask: async (taskId, subtaskData) => {
    const response = await authFetch(`/tasks/${taskId}/subtasks`, {
      method: 'POST',
      body: JSON.stringify(subtaskData),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to create subtask');
    }
    return response.json();
  },

  updateSubtask: async (taskId, subtaskId, subtaskData) => {
    const response = await authFetch(`/tasks/${taskId}/subtasks/${subtaskId}`, {
      method: 'PUT',
      body: JSON.stringify(subtaskData),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to update subtask');
    }
    return response.json();
  },

  removeSubtask: async (taskId, subtaskId) => {
    const response = await authFetch(`/tasks/${taskId}/subtasks/${subtaskId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to delete subtask');
    }
    return response.json();
  },
};

export const resourcesAPI = {
  listByProject: async (projectId) => {
    const response = await authFetch(`/resources?project_id=${encodeURIComponent(projectId)}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch resources');
    }
    return response.json();
  },

  create: async (resourceData) => {
    const response = await authFetch('/resources', {
      method: 'POST',
      body: JSON.stringify(resourceData),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to create resource');
    }
    return response.json();
  },

  update: async (resourceId, resourceData) => {
    const response = await authFetch(`/resources/${resourceId}`, {
      method: 'PUT',
      body: JSON.stringify(resourceData),
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to update resource');
    }
    return response.json();
  },

  remove: async (resourceId) => {
    const response = await authFetch(`/resources/${resourceId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      await throwApiError(response, 'Failed to delete resource');
    }
    return response.json();
  },
};

export const dashboardAPI = {
  getSummary: async (companyId) => {
    const response = await authFetch(`/dashboard/summary?company_id=${encodeURIComponent(companyId)}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch dashboard summary');
    }
    return response.json();
  },
};

const parseFilenameFromDisposition = (dispositionHeader, fallback) => {
  if (!dispositionHeader) return fallback;
  const match = dispositionHeader.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  const encoded = match?.[1];
  const plain = match?.[2];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return fallback;
    }
  }
  return plain || fallback;
};

const sharedReportInFlightRequests = new Map();

export const reportsAPI = {
  createShareLink: async ({
    reportType,
    projectId,
    weeklyReportId,
    dueDateFrom,
    dueDateTo,
    excludeNotStarted = true,
    expirationDays = 7,
  }) => {
    const response = await authFetch('/reports/share-links', {
      method: 'POST',
      body: JSON.stringify({
        reportType,
        projectId,
        weeklyReportId,
        dueDateFrom,
        dueDateTo,
        excludeNotStarted,
        expirationDays,
      }),
    });

    if (!response.ok) {
      await throwApiError(response, 'Failed to generate share link');
    }

    return response.json();
  },

  getSharedReport: async (token) => {
    if (sharedReportInFlightRequests.has(token)) {
      return sharedReportInFlightRequests.get(token);
    }

    const requestPromise = (async () => {
      let response;
      try {
        response = await fetch(`${API_BASE_URL}/reports/shared/${encodeURIComponent(token)}`);
      } catch (error) {
        throwNetworkError();
      }

      if (!response.ok) {
        await throwApiError(response, 'Failed to load shared report', { notify: false });
      }

      return response.json();
    })();

    sharedReportInFlightRequests.set(token, requestPromise);
    try {
      return await requestPromise;
    } finally {
      sharedReportInFlightRequests.delete(token);
    }
  },

  exportWeekly: async ({
    format,
    projectIds,
    dueDateFrom,
    dueDateTo,
    excludeNotStarted = true,
  }) => {
    if (!Array.isArray(projectIds) || projectIds.length === 0) {
      throw createApiError('At least one project is required for export.');
    }

    const params = new URLSearchParams();
    projectIds.forEach((projectId) => params.append('project_ids', String(projectId)));
    if (dueDateFrom) {
      params.set('due_date_from', dueDateFrom);
    }
    if (dueDateTo) {
      params.set('due_date_to', dueDateTo);
    }
    if (excludeNotStarted) {
      params.set('exclude_not_started', 'true');
    }

    const normalizedFormat = format === 'excel' ? 'excel' : 'pdf';
    const response = await authFetch(`/reports/export/${normalizedFormat}?${params.toString()}`, {
      method: 'GET',
    });

    if (!response.ok) {
      await throwApiError(response, `Failed to export ${normalizedFormat.toUpperCase()} report`);
    }

    const blob = await response.blob();
    const defaultName = normalizedFormat === 'excel' ? 'weekly-report.xlsx' : 'weekly-report.pdf';
    const filename = parseFilenameFromDisposition(
      response.headers.get('content-disposition'),
      defaultName,
    );

    return { blob, filename };
  },
};

export const weeklyReportsAPI = {
  getMineByWeek: async (weekStart) => {
    const params = new URLSearchParams({ week_start: String(weekStart) });
    const response = await authFetch(`/weekly-reports/me?${params.toString()}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch weekly activity report');
    }
    return response.json();
  },

  upsert: async ({ weekStart, title, entries }) => {
    const response = await authFetch('/weekly-reports', {
      method: 'POST',
      body: JSON.stringify({
        weekStart,
        title,
        entries,
      }),
    });

    if (!response.ok) {
      await throwApiError(response, 'Failed to save weekly activity report');
    }

    return response.json();
  },

  publish: async (reportId) => {
    const response = await authFetch(`/weekly-reports/${reportId}/publish`, {
      method: 'POST',
    });

    if (!response.ok) {
      await throwApiError(response, 'Failed to publish weekly activity report');
    }

    return response.json();
  },

  getPublishedHistory: async () => {
    const response = await authFetch('/weekly-reports/history/published');
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch published weekly reports');
    }
    return response.json();
  },

  getById: async (reportId) => {
    const response = await authFetch(`/weekly-reports/${reportId}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch weekly activity report details');
    }
    return response.json();
  },

  exportById: async ({ reportId, format }) => {
    const normalizedFormat = format === 'excel' ? 'excel' : 'pdf';
    const response = await authFetch(`/weekly-reports/${reportId}/export/${normalizedFormat}`, {
      method: 'GET',
    });

    if (!response.ok) {
      await throwApiError(response, `Failed to export ${normalizedFormat.toUpperCase()} report`);
    }

    const blob = await response.blob();
    const defaultName = normalizedFormat === 'excel' ? 'weekly-activity-report.xlsx' : 'weekly-activity-report.pdf';
    const filename = parseFilenameFromDisposition(
      response.headers.get('content-disposition'),
      defaultName,
    );

    return { blob, filename };
  },
};

export const activitiesAPI = {
  normalizeItems: (payload) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.items)) {
      return payload.items;
    }

    // Some deployments may wrap payloads in an extra object layer.
    if (Array.isArray(payload?.items?.items)) {
      return payload.items.items;
    }

    if (Array.isArray(payload?.data)) {
      return payload.data;
    }

    return [];
  },

  buildQueryParams: (companyId, {
    search,
    sortBy,
    sortOrder,
    page,
    pageSize,
    action,
    entityType,
    projectId,
    taskId,
  } = {}) => {
    const params = new URLSearchParams({ company_id: companyId });

    if (search) {
      params.set('search', search);
    }
    if (sortBy) {
      params.set('sort_by', sortBy);
    }
    if (sortOrder) {
      params.set('sort_order', sortOrder);
    }
    if (page) {
      params.set('page', String(page));
    }
    if (pageSize) {
      params.set('page_size', String(pageSize));
    }
    if (action) {
      params.set('action', action);
    }
    if (entityType) {
      params.set('entity_type', entityType);
    }
    if (projectId) {
      params.set('project_id', projectId);
    }
    if (taskId) {
      params.set('task_id', taskId);
    }

    return params;
  },

  getActivitiesByCompany: async (companyId, {
    search,
    sortBy,
    sortOrder,
    page,
    pageSize,
    action,
    entityType,
    projectId,
    taskId,
  } = {}) => {
    const params = activitiesAPI.buildQueryParams(companyId, {
      search,
      sortBy,
      sortOrder,
      page,
      pageSize,
      action,
      entityType,
      projectId,
      taskId,
    });

    const response = await authFetch(`/activities?${params.toString()}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch activities');
    }
    const payload = await response.json();
    return activitiesAPI.normalizeItems(payload);
  },

  getActivitiesByCompanyPaginated: async (companyId, {
    search,
    sortBy,
    sortOrder,
    page,
    pageSize,
    action,
    entityType,
    projectId,
    taskId,
  } = {}) => {
    const params = activitiesAPI.buildQueryParams(companyId, {
      search,
      sortBy,
      sortOrder,
      page,
      pageSize,
      action,
      entityType,
      projectId,
      taskId,
    });

    const response = await authFetch(`/activities?${params.toString()}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch activities');
    }

    const payload = await response.json();
    const items = activitiesAPI.normalizeItems(payload);
    const total = Number.isFinite(payload?.total)
      ? payload.total
      : Number.isFinite(payload?.items?.total)
        ? payload.items.total
        : items.length;

    return {
      items,
      total,
      page: payload?.page ?? payload?.items?.page,
      pageSize: payload?.page_size ?? payload?.items?.page_size,
    };
  },

  getById: async (activityId, companyId) => {
    const response = await authFetch(`/activities/${activityId}?company_id=${encodeURIComponent(companyId)}`);
    if (!response.ok) {
      await throwApiError(response, 'Failed to fetch activity detail');
    }
    return response.json();
  },
};

export default authFetch;
